import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from './api';

const AuthCtx = createContext(null);

const MAX_RETRIES = 12;
const RETRY_DELAY = 5000;

// Retries a request when the backend responds with 503 (e.g. Render free-tier
// cold start / instance waking up). Used for auth/me, login, and register so a
// transient wake-up window doesn't surface as a hard failure to the user.
function withRetry(requestFn) {
    const attempt = (attemptNumber) =>
          requestFn().catch((err) => {
            const status = err?.response?.status;
                          const isRetryable = status === 503 || !err.response;
                          if (isRetryable && attemptNumber < MAX_RETRIES) {
                                            return new Promise((resolve) => {
                                                                setTimeout(() => resolve(attempt(attemptNumber + 1)), RETRY_DELAY);
                                            });
                          }
                          throw err;
          });
    return attempt(0);
}

// Cold-start retry used for login/register (and the same pattern in
// CharacterDetail.jsx): up to 4 attempts, 2 seconds apart. Retries on 503
// AND on plain network errors (no response), since a timed-out request
// during a Render cold start looks identical to one - this was the race
// that could surface a false "Authentication failed" toast right after a
// successful register/login. A real, non-retryable error (e.g. bad
// credentials) still fails fast and only shows the user an error once
// every retry has been exhausted.
const AUTH_ACTION_MAX_RETRIES = 4;
const AUTH_ACTION_RETRY_DELAY = 2000;

function withColdStartRetry(requestFn) {
    const attempt = (attemptNumber) =>
        requestFn().catch((err) => {
            const status = err?.response?.status;
            const isRetryable = status === 503 || !err.response;
            if (isRetryable && attemptNumber < AUTH_ACTION_MAX_RETRIES) {
                return new Promise((resolve) => {
                    setTimeout(() => resolve(attempt(attemptNumber + 1)), AUTH_ACTION_RETRY_DELAY);
                });
            }
            throw err;
        });
    return attempt(0);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

  const checkAuth = useCallback((retryFn = withRetry) => {
        return retryFn(() => api.get('/auth/me'))
          .then(({ data }) => {
                    setUser(data);
                    setLoading(false);
          })
          .catch(() => {
                    setUser(null);
                    setLoading(false);
          });
  }, []);

  useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
                // AuthCallback will exchange the session_id and establish the session first.
                if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
                        setLoading(false);
                        return;
                }
        checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
        const { data } = await withColdStartRetry(() => api.post('/auth/login', { email, password }));
        await checkAuth(withColdStartRetry);
        return data;
  };

  const register = async (email, password, name) => {
        const { data } = await withColdStartRetry(() => api.post('/auth/register', { email, password, name }));
        await checkAuth(withColdStartRetry);
        return data;
  };

  const logout = async () => {
        try { await api.post('/auth/logout'); } catch {}
        setUser(null);
  };

  return (
        <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh: checkAuth }}>
          {children}
        </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
